import React, { useState, useEffect, useRef } from 'react';
import './PronunciationFeedback.css';

interface PronunciationFeedbackProps {
  targetPhoneme: string;
  isRecording: boolean;
  onFeedback?: (feedback: FeedbackResult) => void;
}

interface FeedbackResult {
  detected: string;
  confidence: number;
  isCorrect: boolean;
  feedback: string;
  suggestions: string[];
}

// Map phonemes to expected words/sounds for recognition
const PHONEME_RECOGNITION_MAP: Record<string, {
  expectedWords: string[];
  commonMistakes: Record<string, string>;
}> = {
  '/i:/': {
    expectedWords: ['e', 'ee', 'beat', 'see', 'me', 'we'],
    commonMistakes: {
      'i': "You said /ɪ/ (as in 'bit'). Try making the sound longer and smile more.",
      'a': "You said /eɪ/. Focus on a pure 'ee' sound without changing.",
    }
  },
  '/ɪ/': {
    expectedWords: ['i', 'it', 'bit', 'sit', 'hit'],
    commonMistakes: {
      'e': "You said /i:/ (as in 'beat'). Make the sound shorter and more relaxed.",
      'a': "Try positioning your tongue lower and more centered.",
    }
  },
  '/æ/': {
    expectedWords: ['a', 'at', 'cat', 'bat', 'hat'],
    commonMistakes: {
      'e': "You said /e/. Open your mouth wider.",
      'ah': "You said /ɑ:/. Move your tongue forward.",
    }
  },
  '/s/': {
    expectedWords: ['s', 'ss', 'hiss', 'sss'],
    commonMistakes: {
      'z': "You're voicing the sound. Keep it voiceless like a snake hiss.",
      'sh': "You said /ʃ/. Move your tongue forward, closer to your teeth.",
      'th': "Place your tongue behind your teeth, not between them.",
    }
  },
  '/z/': {
    expectedWords: ['z', 'zz', 'buzz', 'zzz'],
    commonMistakes: {
      's': "Add voicing - your vocal cords should vibrate.",
      'th': "You said /ð/. Keep your tongue behind your teeth.",
    }
  },
  '/θ/': {
    expectedWords: ['th', 'think', 'theta'],
    commonMistakes: {
      's': "Put your tongue between your teeth, not behind them.",
      'f': "Use your tongue and teeth, not lips and teeth.",
      't': "Don't stop the airflow - let it flow between tongue and teeth.",
    }
  },
  '/ð/': {
    expectedWords: ['th', 'the', 'this', 'that'],
    commonMistakes: {
      'z': "Put your tongue between your teeth.",
      'd': "Don't stop the airflow completely - let it flow between tongue and teeth.",
      'v': "Use your tongue and teeth, not lips and teeth.",
    }
  },
  '/ʃ/': {
    expectedWords: ['sh', 'shh', 'shush'],
    commonMistakes: {
      's': "Pull your tongue back and round your lips slightly.",
      'ch': "Don't stop the airflow - keep it continuous.",
    }
  },
  '/tʃ/': {
    expectedWords: ['ch', 'chat', 'church'],
    commonMistakes: {
      'sh': "Start with a 't' sound before the 'sh'.",
      't': "Add the 'sh' sound after the 't'.",
    }
  },
  '/r/': {
    expectedWords: ['r', 'rr', 'red', 'run'],
    commonMistakes: {
      'w': "Don't round your lips as much. Curl your tongue back.",
      'l': "Don't touch the roof of your mouth - curl or bunch your tongue.",
    }
  },
  '/l/': {
    expectedWords: ['l', 'll', 'la', 'let'],
    commonMistakes: {
      'r': "Touch your tongue tip to the ridge behind your upper teeth.",
      'w': "Use your tongue, not just your lips.",
    }
  },
};

const PronunciationFeedback: React.FC<PronunciationFeedbackProps> = ({
  targetPhoneme,
  isRecording,
  onFeedback
}) => {
  const [detectedText, setDetectedText] = useState<string>('');
  const [confidence, setConfidence] = useState<number>(0);
  const [feedbackResult, setFeedbackResult] = useState<FeedbackResult | null>(null);
  const [isListening, setIsListening] = useState(false);
  
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    // Check if browser supports speech recognition
    const SpeechRecognition = (window as any).SpeechRecognition || 
                             (window as any).webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.warn('Speech recognition not supported');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 3;
    recognition.lang = 'en-US';

    recognition.onresult = (event: any) => {
      const result = event.results[0];
      const transcript = result[0].transcript.toLowerCase().trim();
      const conf = result[0].confidence || 0.5;
      
      setDetectedText(transcript);
      setConfidence(conf);
      
      // Analyze the result
      const feedback = analyzePronunciation(targetPhoneme, transcript, conf);
      setFeedbackResult(feedback);
      
      if (onFeedback) {
        onFeedback(feedback);
      }
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'no-speech') {
        setFeedbackResult({
          detected: '',
          confidence: 0,
          isCorrect: false,
          feedback: "No speech detected. Make sure to speak clearly into the microphone.",
          suggestions: ["Speak louder", "Get closer to the microphone", "Check your microphone settings"]
        });
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
          // Ignore errors on cleanup
        }
      }
    };
  }, [targetPhoneme, onFeedback]);

  useEffect(() => {
    if (isRecording && recognitionRef.current) {
      try {
        recognitionRef.current.start();
        setIsListening(true);
        setFeedbackResult(null);
        setDetectedText('');
      } catch (e) {
        console.error('Error starting recognition:', e);
      }
    } else if (!isRecording && recognitionRef.current && isListening) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.error('Error stopping recognition:', e);
      }
    }
  }, [isRecording, isListening]);

  const analyzePronunciation = (
    target: string, 
    detected: string, 
    confidence: number
  ): FeedbackResult => {
    const phonemeData = PHONEME_RECOGNITION_MAP[target];
    
    if (!phonemeData) {
      return {
        detected,
        confidence,
        isCorrect: false,
        feedback: "Analysis not available for this phoneme yet.",
        suggestions: []
      };
    }

    // Check if detected text matches expected words
    const isCorrect = phonemeData.expectedWords.some(word => 
      detected.includes(word)
    );

    if (isCorrect) {
      return {
        detected,
        confidence,
        isCorrect: true,
        feedback: "Great job! You pronounced it correctly! 🎉",
        suggestions: ["Try it again to build consistency", "Record multiple times to practice"]
      };
    }

    // Check for common mistakes
    let specificFeedback = "The sound wasn't quite right.";
    const suggestions: string[] = [];

    for (const [mistake, tip] of Object.entries(phonemeData.commonMistakes)) {
      if (detected.includes(mistake)) {
        specificFeedback = tip;
        break;
      }
    }

    // General suggestions based on what was detected
    if (detected.length === 0) {
      suggestions.push("Speak louder and clearer");
      suggestions.push("Make sure the microphone is working");
    } else {
      suggestions.push("Listen to the coach examples again");
      suggestions.push("Focus on the mouth position");
      suggestions.push("Try speaking more slowly");
    }

    return {
      detected: detected || '(no speech detected)',
      confidence,
      isCorrect: false,
      feedback: specificFeedback,
      suggestions
    };
  };

  if (!feedbackResult) {
    return null;
  }

  return (
    <div className={`pronunciation-feedback ${feedbackResult.isCorrect ? 'correct' : 'needs-work'}`}>
      <div className="feedback-header">
        <span className="feedback-icon">
          {feedbackResult.isCorrect ? '✅' : '💭'}
        </span>
        <h4>Pronunciation Feedback</h4>
      </div>
      
      <div className="feedback-content">
        <div className="detection-info">
          <p className="detected-text">
            <strong>What we heard:</strong> "{feedbackResult.detected}"
          </p>
          {feedbackResult.confidence > 0 && (
            <div className="confidence-meter">
              <span>Confidence:</span>
              <div className="confidence-bar">
                <div 
                  className="confidence-fill" 
                  style={{ width: `${feedbackResult.confidence * 100}%` }}
                />
              </div>
              <span>{Math.round(feedbackResult.confidence * 100)}%</span>
            </div>
          )}
        </div>

        <div className="feedback-message">
          <p className={feedbackResult.isCorrect ? 'success-message' : 'improvement-message'}>
            {feedbackResult.feedback}
          </p>
        </div>

        {feedbackResult.suggestions.length > 0 && (
          <div className="suggestions">
            <strong>Tips:</strong>
            <ul>
              {feedbackResult.suggestions.map((suggestion, index) => (
                <li key={index}>{suggestion}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default PronunciationFeedback;